import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function ConfessionsPage() {
  useEffect(() => {
    document.title = "Confessions — Junk4Journals | Write What You Cannot Say";
    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", "Send your confession by mail. No profiles, no timelines — just paper, ink, and the quiet relief of telling the truth. The Postmark Club receives your letters with care.");
    setMeta("og:title", "Confessions — Junk4Journals", true);
    setMeta("og:description", "Some things are easier to say on paper. Send your confession by mail to The Postmark Club.", true);

    const existing = document.getElementById("schema-confessions");
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.id = "schema-confessions";
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Confessions — Junk4Journals",
      "description": "Send your confession by mail. No profiles, no timelines — just paper, ink, and the quiet relief of telling the truth.",
      "url": "https://junk4journals.com/confessions",
    });
    document.head.appendChild(script);

    return () => {
      document.getElementById("schema-confessions")?.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#faf8f4]">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-36 pb-20 px-6">
        <div className="max-w-xl mx-auto text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#b89a78] mb-6">
            The Postmark Club
          </p>
          <h1
            className="text-5xl md:text-6xl font-bold text-[#2c2016] mb-8 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Confessions
          </h1>
          <p
            className="text-xl text-[#6b5a4a] mb-8 leading-snug"
            style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic" }}
          >
            Some things are easier to say on paper.
          </p>
          <p className="text-base text-[#7a6a58] leading-relaxed">
            There are things we carry that were never meant to be held forever.
            This is a place to set them down. No noise. No profiles. No timelines.
            Just paper, ink, and the quiet relief of telling the truth.
          </p>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-xs mx-auto px-6">
        <div className="border-t border-[#e0d6c8]"></div>
      </div>

      {/* Mailing Instructions */}
      <section className="py-20 px-6">
        <div className="max-w-xl mx-auto">
          <div className="bg-[#f3ede3] border border-[#e0d6c8] rounded-lg px-10 py-10 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#b89a78] mb-5">
              Send your confession to
            </p>
            <address
              className="not-italic text-[#3a2e1e] leading-loose"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              <span className="block text-lg font-semibold">The Postmark Club</span>
              <span className="block text-base text-[#6b5a4a]">6510-A S. Academy Blvd #321</span>
              <span className="block text-base text-[#6b5a4a]">Colorado Springs, CO 80906</span>
            </address>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-xs mx-auto px-6">
        <div className="border-t border-[#e0d6c8]"></div>
      </div>

      {/* Options / User Control */}
      <section className="py-20 px-6">
        <div className="max-w-xl mx-auto">
          <h2
            className="text-2xl font-bold text-[#2c2016] mb-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            You choose how your letter is handled
          </h2>
          <p className="text-sm text-[#9a8878] mb-8 leading-relaxed">
            There is no wrong answer. Whatever feels right to you is right.
          </p>
          <ul className="space-y-5">
            {[
              "Send it anonymously with no return address or identifying details",
              "Give permission for your letter to be shared",
              "Ask that your letter remain completely private",
            ].map((option, i) => (
              <li key={i} className="flex items-start gap-4">
                <span className="mt-1 w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-quill-pen-line text-[#b89a78] text-base"></i>
                </span>
                <span className="text-[#5a4a3a] text-base leading-relaxed">{option}</span>
              </li>
            ))}
          </ul>

          {/* Important note */}
          <div className="mt-10 pt-8 border-t border-[#e0d6c8]">
            <p className="text-sm text-[#8a7a6a] leading-relaxed text-center italic" style={{ fontFamily: "'Playfair Display', serif" }}>
              Your identity will never be shared without your permission.
            </p>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-xs mx-auto px-6">
        <div className="border-t border-[#e0d6c8]"></div>
      </div>

      {/* Guidelines */}
      <section className="py-20 px-6">
        <div className="max-w-xl mx-auto">
          <h2
            className="text-2xl font-bold text-[#2c2016] mb-8"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            A few gentle guidelines
          </h2>
          <ul className="space-y-6">
            {[
              { icon: "ri-edit-line", text: "Handwritten is preferred, but typed and printed is welcome" },
              { icon: "ri-file-text-line", text: "Write as little or as much as you need" },
              { icon: "ri-heart-line", text: "There is no right way to do this" },
              { icon: "ri-chat-quote-line", text: "Say the thing you haven\u2019t said" },
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-4">
                <span className="mt-0.5 w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-[#c9a87c] text-base`}></i>
                </span>
                <span className="text-[#5a4a3a] text-base leading-relaxed">{item.text}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-xs mx-auto px-6">
        <div className="border-t border-[#e0d6c8]"></div>
      </div>

      {/* Why This Exists */}
      <section className="py-20 px-6">
        <div className="max-w-xl mx-auto">
          <h2
            className="text-2xl font-bold text-[#2c2016] mb-7"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Why this exists
          </h2>
          <p className="text-base text-[#6b5a4a] leading-loose">
            Not everything belongs online. Some things deserve a slower path. A letter gives weight
            to what we carry. It creates a moment where something hidden can finally be released.
            You don&apos;t need a response. You don&apos;t need an audience. Sometimes it is enough to send it.
          </p>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-xs mx-auto px-6">
        <div className="border-t border-[#e0d6c8]"></div>
      </div>

      {/* Optional Sharing */}
      <section className="py-20 px-6">
        <div className="max-w-xl mx-auto">
          <h2
            className="text-2xl font-bold text-[#2c2016] mb-7"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Optional sharing
          </h2>
          <p className="text-base text-[#6b5a4a] leading-loose">
            Some letters may be featured in The Postmark Club or The Postmark Letter.
            Names and identifying details will always be removed unless you explicitly
            give permission otherwise.
          </p>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-xs mx-auto px-6">
        <div className="border-t border-[#e0d6c8]"></div>
      </div>

      {/* Closing CTA */}
      <section className="py-24 px-6">
        <div className="max-w-xl mx-auto text-center">
          <p
            className="text-3xl md:text-4xl font-bold text-[#2c2016] leading-snug mb-5"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Write it. Seal it. Send it.
          </p>
          <p className="text-base text-[#8a7a6a] italic" style={{ fontFamily: "'Playfair Display', serif" }}>
            We&apos;ll be here to receive it.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
